import { NavLink, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import LoginPage from "./auth/LoginPage";
import StatusPage from "./pages/StatusPage";
import RulesPage from "./pages/RulesPage";
import ConfigPage from "./pages/ConfigPage";
import { LogOut, Settings2, Sliders, Home } from "lucide-react";
import { useStatus } from "./api/queries";

function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) {
    return <div className="flex min-h-full items-center justify-center text-sm text-slate-400">Loading…</div>;
  }
  if (!auth.authenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <AppShell />;
}

function AppShell() {
  const auth = useAuth();
  const status = useStatus();
  const version = status.data?.server.version;
  const devMode = status.data?.server.dev_mode;
  return (
    <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-3 w-3 rounded-full bg-brand shadow-[0_0_12px_theme(colors.brand.DEFAULT)]" />
          <h1 className="text-lg font-semibold">Office status LED Controller</h1>
          {version && <span className="text-xs text-slate-400">v{version}</span>}
          {devMode && (
            <span className="rounded-md border border-amber-500/60 bg-amber-500/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-300">
              Dev mode
            </span>
          )}
        </div>
        <nav className="flex flex-wrap gap-1 text-sm">
          <NavBtn to="/" label="Status" icon={<Home className="h-4 w-4" />} end />
          <NavBtn to="/rules" label="Rules" icon={<Settings2 className="h-4 w-4" />} />
          <NavBtn to="/config" label="Configuration" icon={<Sliders className="h-4 w-4" />} />
          <button
            type="button"
            onClick={() => void auth.signOut()}
            className="ml-2 flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm hover:border-slate-500"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">
              Sign out{auth.username ? ` (${auth.username})` : ""}
            </span>
          </button>
        </nav>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

function NavBtn({
  to,
  label,
  icon,
  end,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-1 rounded-md border px-3 py-1.5 ${
          isActive
            ? "border-brand bg-brand/20 text-brand"
            : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route index element={<StatusPage />} />
        <Route path="rules" element={<RulesPage />} />
        <Route path="config" element={<ConfigPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
